import { generatePath, useParams } from 'react-router-dom';
import { useCallback } from 'react';

import { encodeUrl } from '../../common/helpers';
import useLocale from '../../common/useLocale';

type PartialRouteParams = Partial<Record<keyof RouteParams, string>>;

export const Routes = {
  // Website
  Root: '/',
  Home: '/:locale',
  Calendar: '/:locale/calendar/:sportType?',

  // Guest
  Login: '/login',
  Unsubscribe: '/unsubscribe/:email/:token/:type',

  // Portal
  Portal: '/me',
  Account: '/me/account',
  AccountDelete: '/me/account/delete',
  AccountMailPreferences: '/me/account/mail-preferences',

  // Back-end
  Register: '/b/register',

  // Registrations
  Registrations: '/p/:projectId/:participantId/events/:eventId/:token',
  UnassignedRegistrations: '/p/:projectId/:participantId/events/:eventId/:token/unassigned/:ticketId',
  Upgrades: '/p/:projectId/:participantId/events/:eventId/:token/upgrades',
  MailPreferences: '/p/:projectId/:participantId/mail-preferences',

  ClaimRegistration: '/p/:projectId/claim/:participantId/:eventId/registration/:registrationId/:claimToken',
  ClaimRegistrations: '/p/:projectId/claim/:participantId/:eventId/:claimToken',
  ClaimSuccess: '/p/:projectId/claim/success/:eventId',

  // Participant details
  Orders: '/p/:projectId/:participantId/events/:eventId/:token/orders',
  Order: '/p/:projectId/:participantId/events/:eventId/:token/orders/:orderId',
  Charity: '/p/:projectId/:participantId/events/:eventId/:token/charity',
};

export type RoutesKey = keyof typeof Routes;

interface RouteParams {
  locale?: string;
  projectId?: string;
  eventId?: string;
  ownerId?: string;
  participantId?: string;
  registrationId?: string;
  token?: string;
  orderId?: string;
  claimToken?: string;
  ticketId?: string;
  sportType?: string;
}

interface RouteOptions {
  search?: object;
  withEmail?: string;
  withReturnUrl?: true;
}

export const useNavigation = () => {
  const routeParams = useParams<RouteParams>();
  const { locale } = useLocale();

  const route = useCallback((
    routeKey: RoutesKey,
    params?: PartialRouteParams,
    options: RouteOptions = {},
  ) => {
    const path = generatePath(Routes[routeKey], { ...routeParams, locale, ...params });

    const query = {
      ...options.search,
      ...(options.withEmail ? { email: options.withEmail } : {}),
      ...(options.withReturnUrl ? { returnUrl: window.location.href } : {}),
    };

    return Object.keys(query).length ? `${path}?${encodeUrl(query)}` : path;
  }, [routeParams, locale]);

  return {
    r: route,
    routeParams,
  };
};
